/**
 * ApprovalWorkflowTab Component
 * Displays all pending approval generations
 * Allows inline editing and approval/rejection
 * Allows opening affaire for regeneration
 */

import React, { useState } from 'react'
import {
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  FolderOpenIcon
} from '@heroicons/react/24/outline'
import { usePendingApprovals, useApproveGeneration, useRejectGeneration } from '../hooks/useIA'
import type { PendingApproval } from '../types/ia.types'

interface ApprovalWorkflowTabProps {
  onOpenAffaire: (affaireId: number, adresse: string) => void
}

export const ApprovalWorkflowTab: React.FC<ApprovalWorkflowTabProps> = ({ onOpenAffaire }) => {
  const { data: pendingApprovals = [], isLoading, error } = usePendingApprovals()
  const approveGeneration = useApproveGeneration()
  const rejectGeneration = useRejectGeneration()

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [editingItems, setEditingItems] = useState<Map<string, string>>(new Map())
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const startEditing = (id: string, currentText: string) => {
    setEditingItems(prev => new Map(prev).set(id, currentText))
  }

  const cancelEditing = (id: string) => {
    setEditingItems(prev => {
      const newMap = new Map(prev)
      newMap.delete(id)
      return newMap
    })
  }

  const handleTextChange = (id: string, newText: string) => {
    setEditingItems(prev => new Map(prev).set(id, newText))
  }

  const handleApprove = async (approval: PendingApproval) => {
    const editedText = editingItems.get(approval.id) || approval.generatedText

    if (!editedText.trim()) {
      return
    }

    await approveGeneration.mutateAsync({
      historyId: approval.id,
      request: { editedText: editedText.trim() }
    })

    // Clean up editing state
    setEditingItems(prev => {
      const newMap = new Map(prev)
      newMap.delete(approval.id)
      return newMap
    })
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      newSet.delete(approval.id)
      return newSet
    })
  }

  const handleReject = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir rejeter cette génération ?')) {
      return
    }

    await rejectGeneration.mutateAsync(id)

    // Clean up state
    setEditingItems(prev => {
      const newMap = new Map(prev)
      newMap.delete(id)
      return newMap
    })
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      newSet.delete(id)
      return newSet
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Bulk approve all pending approvals
  const handleBulkApprove = async () => {
    if (pendingApprovals.length === 0) return

    if (!confirm(`Êtes-vous sûr de vouloir approuver toutes les ${pendingApprovals.length} générations en attente ?`)) {
      return
    }

    setIsBulkProcessing(true)
    try {
      // Approve all in parallel
      await Promise.all(
        pendingApprovals.map(approval =>
          approveGeneration.mutateAsync({
            historyId: approval.id,
            request: { editedText: approval.generatedText }
          })
        )
      )
    } finally {
      setIsBulkProcessing(false)
      // Clear editing and expanded states
      setEditingItems(new Map())
      setExpandedItems(new Set())
    }
  }

  // Bulk reject all pending approvals
  const handleBulkReject = async () => {
    if (pendingApprovals.length === 0) return

    if (!confirm(`Êtes-vous sûr de vouloir rejeter toutes les ${pendingApprovals.length} générations en attente ? Ces affaires réapparaîtront dans les suggestions.`)) {
      return
    }

    setIsBulkProcessing(true)
    try {
      // Reject all in parallel
      await Promise.all(
        pendingApprovals.map(approval =>
          rejectGeneration.mutateAsync(approval.id)
        )
      )
    } finally {
      setIsBulkProcessing(false)
      // Clear editing and expanded states
      setEditingItems(new Map())
      setExpandedItems(new Set())
    }
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <p className="text-red-700">
          Erreur lors du chargement des approbations en attente
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-md border-2 border-gray-200 p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <ClockIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Approbations en attente
              </h2>
              <p className="text-sm text-gray-600">
                {isLoading ? 'Chargement...' : `${pendingApprovals.length} génération(s) à approuver`}
              </p>
            </div>
          </div>

          {/* Bulk Action Buttons */}
          {!isLoading && pendingApprovals.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkReject}
                disabled={isBulkProcessing}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm font-medium rounded-lg hover:bg-red-100 active:bg-red-200 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {isBulkProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                    Traitement...
                  </>
                ) : (
                  <>
                    <XCircleIcon className="w-4 h-4" />
                    Rejeter tout
                  </>
                )}
              </button>

              <button
                onClick={handleBulkApprove}
                disabled={isBulkProcessing}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 active:bg-green-800 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              >
                {isBulkProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Traitement...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4" />
                    Approuver tout
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="bg-white rounded-xl p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500">Chargement des approbations...</p>
        </div>
      ) : pendingApprovals.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center border-2 border-gray-200">
          <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Aucune approbation en attente</p>
          <p className="text-sm text-gray-500 mt-1">
            Toutes les générations ont été traitées
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingApprovals.map((approval) => {
            const isExpanded = expandedItems.has(approval.id)
            const isEditing = editingItems.has(approval.id)
            const editedText = editingItems.get(approval.id) || approval.generatedText
            const isProcessing = approveGeneration.isPending || rejectGeneration.isPending

            return (
              <div
                key={approval.id}
                className="bg-white rounded-2xl shadow-md border-2 border-gray-200 overflow-hidden"
              >
                {/* Header - always visible */}
                <div className="p-5 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-200">
                  <div className="flex items-start justify-between gap-4">
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => toggleExpand(approval.id)}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Affaire #{approval.affaireId}
                        </span>
                        <span className="text-sm text-gray-600">
                          {approval.adresse}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Généré par: {approval.generatedByName}</span>
                        <span>Le: {formatDate(approval.createdAt)}</span>
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                          {approval.model}
                        </span>
                      </div>
                    </div>

                    <div className="flex-shrink-0 flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onOpenAffaire(approval.affaireId, approval.adresse)
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 shadow-sm"
                      >
                        <FolderOpenIcon className="w-4 h-4" />
                        Ouvrir
                      </button>

                      <button
                        onClick={() => toggleExpand(approval.id)}
                        className="p-2 hover:bg-amber-200 rounded-lg transition-colors duration-150"
                      >
                        {isExpanded ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-600" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="p-5 space-y-4">
                    {/* Generated text */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">
                          Texte généré
                        </label>
                        {!isEditing && (
                          <button
                            onClick={() => startEditing(approval.id, approval.generatedText)}
                            disabled={isProcessing}
                            className="flex items-center gap-1 px-3 py-1 text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-all duration-150 disabled:opacity-50"
                          >
                            <PencilIcon className="w-4 h-4" />
                            Modifier
                          </button>
                        )}
                      </div>

                      {isEditing ? (
                        <textarea
                          value={editedText}
                          onChange={(e) => handleTextChange(approval.id, e.target.value)}
                          disabled={isProcessing}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-sm leading-relaxed disabled:opacity-50 disabled:bg-gray-50"
                          rows={12}
                        />
                      ) : (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {approval.generatedText}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        {isEditing && (
                          <button
                            onClick={() => cancelEditing(approval.id)}
                            disabled={isProcessing}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-150 disabled:opacity-50"
                          >
                            Annuler
                          </button>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleReject(approval.id)}
                          disabled={isProcessing}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <XCircleIcon className="w-5 h-5" />
                          Rejeter
                        </button>

                        <button
                          onClick={() => handleApprove(approval)}
                          disabled={isProcessing || !editedText.trim()}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CheckCircleIcon className="w-5 h-5" />
                          {isEditing ? 'Approuver et sauvegarder' : 'Approuver'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
